import React, { createContext, useContext, useState, ReactNode } from 'react';

interface Alert {
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
    duration?: number;
}

interface AlertContextType {
    alerts: Alert[];
    showAlert: (type: Alert['type'], message: string, duration?: number) => void;
    removeAlert: (id: string) => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlert = () => {
    const context = useContext(AlertContext);
    if(context === undefined){
        throw new Error('useAlert must be used within an AlertProvider');
    }
    return context;
};

interface AlertProviderProps {
    children: ReactNode;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({children}) => {
    const [alerts, setAlerts] = useState<Alert[]>([]);

    const showAlert = (type: Alert['type'], message: string, duration = 5000) => {
        const id = Math.random().toString(36).substr(2, 9);
        const newAlert: Alert = {id, type, message, duration};
        
        setAlerts(prev => [...prev, newAlert]);

        if(duration > 0){
            setTimeout(() => {
                removeAlert(id);
            }, duration);
        }
    };

    const removeAlert = (id: string) => {
        setAlerts(prev => prev.filter(alert => alert.id !== id));
    };

    const value: AlertContextType = {
        alerts,
        showAlert,
        removeAlert,
    };

    return (
        <AlertContext.Provider value={value}>
            {children}
            <AlertContainer />
        </AlertContext.Provider>
    );
};

const AlertContainer: React.FC = () => {
    const {alerts, removeAlert} = useAlert();

    return (
        <div className="fixed top-20 right-4 z-[9999] space-y-2 pointer-events-none flex flex-col items-end">
            {alerts.map(alert => (
                <div
                    key={alert.id}
                    className={`pointer-events-auto p-3 min-w-[300px] max-w-sm transition-all duration-300 border-l-2 bg-[#0a0a0a]/95 backdrop-blur-md shadow-[0_4px_20px_rgba(0,0,0,0.5)] flex items-start justify-between relative overflow-hidden group ${
                        alert.type === 'success' ? 'border-emerald-500' :
                        alert.type === 'error' ? 'border-red-500' :
                        alert.type === 'warning' ? 'border-yellow-500' :
                        'border-cyan-500'
                    }`}
                >
					{/* Scanning line animation */}
					<div className={`absolute inset-0 opacity-10 pointer-events-none ${
                        alert.type === 'success' ? 'bg-[linear-gradient(transparent_50%,rgba(16,185,129,0.5)_50%)] bg-[length:100%_4px]' :
                        alert.type === 'error' ? 'bg-[linear-gradient(transparent_50%,rgba(239,68,68,0.5)_50%)] bg-[length:100%_4px]' :
                        alert.type === 'warning' ? 'bg-[linear-gradient(transparent_50%,rgba(234,179,8,0.5)_50%)] bg-[length:100%_4px]' :
                        'bg-[linear-gradient(transparent_50%,rgba(6,182,212,0.5)_50%)] bg-[length:100%_4px]'
					}`} />
					
                    <div className="flex flex-col z-10 w-full pl-1">
						<div className="flex justify-between items-start mb-1">
							<span className={`text-[9px] font-mono uppercase tracking-widest font-bold ${
								alert.type === 'success' ? 'text-emerald-500' :
								alert.type === 'error' ? 'text-red-500' :
								alert.type === 'warning' ? 'text-yellow-500' :
								'text-cyan-500'
							}`}>
								{alert.type === 'success' ? '[SYS.OK]' :
								alert.type === 'error' ? '[SYS.ERR]' :
								alert.type === 'warning' ? '[SYS.WARN]' :
								'[SYS.INFO]'}
							</span>
							<button
								onClick={() => removeAlert(alert.id)}
								className={`text-neutral-500 hover:text-white transition-colors p-0.5 ml-2`}
							>
								×
							</button>
						</div>
                        <p className="text-[11px] font-mono leading-relaxed text-neutral-200">{alert.message}</p>
                    </div>
                </div>
            ))}
        </div>
    );
};
